import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { conditionKeys, FILL_TYPES, transportationKeys } from '../../consts/conditions';
import { operatorKeys } from '../../consts/operators';
import { deepCopy } from '../../utils/deepCopy';

// Actions whose "When" condition value should default to Column (not Input).
const COLUMN_DEFAULT_VALUE_ACTIONS = [
  transportationKeys.FILTER,
  transportationKeys.SUM_IF,
  transportationKeys.DUPLICATE_ROWS,
  transportationKeys.DATE_SHIFTING,
  transportationKeys.SPLIT,
  transportationKeys.REPLACE
];

export const reorderList = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);

  result.splice(endIndex, 0, removed);

  return result;
};

export function findAllObjects(obj) {
  const result = [];

  function traverse(currentObj) {
    // Add the current object itself
    result.push(currentObj);

    // Check if 'conditions' property exists and is an array
    if (currentObj?.conditions?.length > 0) {
      // Recursively process each object in the 'conditions' array
      currentObj?.conditions.forEach(innerObj => {
        traverse(innerObj);
      });
    }
  }

  // Start the traversal with the initial object
  traverse(obj);

  return result;
}

export function removeObjectByIds(array, idsToRemove) {
  // Filter out objects whose id is in idsToRemove
  const filteredArray = array.filter(obj => !idsToRemove.includes(obj.id));

  // Iterate over the filtered array to handle nested arrays
  filteredArray.forEach(obj => {
    Object.keys(obj).forEach(key => {
      if (Array.isArray(obj[key])) {
        obj[key] = removeObjectByIds(obj[key], idsToRemove);
      }
    });
  });

  return filteredArray;
}

export function updateObjectById(array, idToFind, updates) {
  array.forEach(obj => {
    // Check if the current object is the one to update
    if (obj?.id === idToFind) {
      // Apply all updates to this object

      obj = { ...updates };
    }

    // Recursively call this function for all properties which are arrays of objects
    Object.keys(obj || {}).forEach(key => {
      if (Array.isArray(obj[key])) {
        updateObjectById(obj[key], idToFind, updates);
      }
    });
  });
}

export const getMonthValue = (period, idx, year) => {
  let monthIndex = idx + 1;

  if (period === 'Q2') monthIndex += 3;
  if (period === 'Q3') monthIndex += 6;
  if (period === 'Q4') monthIndex += 9;

  if (period === 'H2') monthIndex += 6;
  if (period === 'X2') monthIndex += 3;

  return `01.${monthIndex > 9 ? '' : '0'}${monthIndex}.${year}`;
};

export const _filterTypeItem = {
  id: uuidv4(),
  function: null,
  column: null,
  inputType: 'input',
  operator: null,
  columns: [],
  type: 'item',
  errors: [],
  value: {
    input: '',
    column: '',
    null: null,
    list: [],
    ai_input: {
      browser_use: false,
      documents: [],
      input: '',
      selected_columns: []
    }
  }
};

export const _filterType = {
  id: uuidv4(),
  firstParent: true,
  label: '',
  conditionLabel: conditionKeys.AND,
  type: 'group',
  inputType: 'input',
  errors: [],
  columns: [],
  columns2: [],
  then: {
    input: '',
    column: '',
    null: null
  },
  elseValue: {
    input: '',
    column: '',
    null: null
  },
  else: null,
  elseInputType: 'input',
  thenInputType: 'input',
  replaceColumn: '',
  conditions: [
    {
      ...Object.assign({}, _filterTypeItem)
    }
  ]
};

export default function useDefaultTransformation({
  setSelectedTransformation,
  columns,
  reset,
  periodName,
  year
}) {
  const [filterTypes, setFilterTypes] = useState<any[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [disableMessage, setDisableMessage] = useState<string | undefined>();

  useEffect(() => {
    return () => {
      setFilterTypes([]);
      setSelectedItems([]);
    };
  }, []);

  const isArithmetic = filterTypes?.[0]?.label === transportationKeys.ARITHMETIC;
  const isConcatenate = filterTypes?.[0]?.label === transportationKeys.CONCATENATE;

  const onSelectFilterType = value => {
    const newId = uuidv4();
    setSelectedTransformation(null);

    switch (value) {
      case transportationKeys.ARITHMETIC:
        setFilterTypes([
          {
            ..._filterType,
            id: newId,
            label: value,
            isNew: true,
            isParent: true,
            then: {
              input: '',
              column: '',
              null: null
            },
            elseValue: {
              input: '',
              column: '',
              null: null
            },
            elseInputType: 'column',
            thenInputType: 'column',
            replaceColumn: '',
            conditions: [
              {
                ...deepCopy(_filterTypeItem),
                id: uuidv4(),
                inputType: 'column'
              }
            ]
          }
        ]);
        break;
      case transportationKeys.DUPLICATE_ROWS:
        setFilterTypes([
          {
            ..._filterType,
            columns: ['Split 1', 'Split 2'],
            id: newId,
            label: value,
            isNew: true,
            isParent: true,
            then: {
              input: '',
              column: '',
              null: null
            },
            elseValue: {
              input: '',
              column: '',
              null: null
            },
            elseInputType: 'input',
            thenInputType: 'input',
            replaceColumn: 1,
            conditions: [
              {
                ...deepCopy(_filterTypeItem),
                id: uuidv4(),
                inputType: 'column'
              }
            ]
          }
        ]);
        break;
      case transportationKeys.DATE_SHIFTING:
        setFilterTypes([
          {
            ..._filterType,
            columns: [],
            id: newId,
            label: value,
            isNew: true,
            isParent: true,
            then: {
              input: '',
              column: '',
              null: null
            },
            elseValue: {
              input: '',
              column: '',
              null: null
            },

            columnName: '',
            shiftingMethod: '',
            timePeriod: 'month',
            periodAmount: '',

            elseInputType: 'input',
            thenInputType: 'input',
            replaceColumn: '',
            conditions: [
              {
                ...deepCopy(_filterTypeItem),
                id: uuidv4(),
                inputType: 'column'
              }
            ]
          }
        ]);
        break;

      case transportationKeys.SPLIT:
        setFilterTypes([
          {
            ..._filterType,
            columns: ['Split 1', 'Split 2'],
            id: newId,
            label: value,
            isNew: true,
            isParent: true,
            then: {
              input: '',
              column: '',
              null: null
            },
            elseValue: {
              input: '',
              column: '',
              null: null
            },
            elseInputType: 'input',
            thenInputType: 'input',
            replaceColumn: '',
            errors: [],
            conditions: [
              {
                ...deepCopy(_filterTypeItem),
                id: uuidv4(),
                inputType: 'column'
              }
            ]
          }
        ]);
        break;
      case transportationKeys.SPLIT_BY_VALUE:
        setFilterTypes([
          {
            ..._filterType,
            columns: [],
            id: newId,
            label: value,
            isNew: true,
            isParent: true,
            then: {
              input: '',
              column: '',
              null: null
            },
            elseValue: {
              input: '',
              column: '',
              null: null
            },
            elseInputType: 'input',
            thenInputType: 'input',
            replaceColumn: '',
            numberOfSplits: 0,
            direction: 'left_to_right', // left_to_right, right_to_left
            expandedNewColumns: false,
            errors: [],
            conditions: [
              {
                ...deepCopy(_filterTypeItem),
                id: uuidv4(),
                inputType: 'column'
              }
            ]
          }
        ]);
        break;
      case transportationKeys.JOIN:
      case transportationKeys.FILTER:
      case transportationKeys.SUM_IF:
      case transportationKeys.REPLACE:
        setFilterTypes([
          {
            ..._filterType,
            id: newId,
            label: value,
            isNew: true,
            isParent: true,
            then: {
              input: '',
              column: '',
              null: null,
              ai_input: {
                browser_use: false,
                documents: [],
                input: '',
                selected_columns: []
              }
            },
            elseValue: {
              input: '',
              column: '',
              null: null
            },
            elseInputType: value === transportationKeys.REPLACE ? 'column' : 'input',
            thenInputType: value === transportationKeys.REPLACE ? 'column' : 'input',
            replaceColumn: '',
            conditions: [
              {
                ...deepCopy(_filterTypeItem),
                id: uuidv4(),
                inputType: 'column',
                fuzzyMatching: false
              }
            ]
          }
        ]);
        break;

      case transportationKeys.ADD_COLUMN:
        setFilterTypes([
          {
            ...deepCopy(_filterTypeItem),
            value: { input: null, column: null, null: null, list: [] },
            id: newId,
            columns: columns,
            isNew: true,
            label: value,
            inputType: 'input'
          }
        ]);
        break;
      case transportationKeys.RENAME_COLUMNS:
        setFilterTypes([
          {
            ...deepCopy(_filterTypeItem),
            value: { input: null, column: null, null: null, list: [] },
            id: newId,
            columns: columns,
            isNew: true,
            label: value,
            inputType: 'input'
          }
        ]);
        break;
      case transportationKeys.ABSOLUTE:
      case transportationKeys.REMOVE_DUPLICATES:
      case transportationKeys.CHANGE_DATA_TYPES:
        setFilterTypes([
          {
            ...deepCopy(_filterTypeItem),
            value: { input: null, column: null, null: null, list: [] },
            id: newId,
            columns: columns,
            isNew: true,
            label: value,
            inputType: 'column',
            currentFormats: [],
            outputFormat: null
          }
        ]);
        break;
      case transportationKeys.NEGATIVE:
        setFilterTypes([
          {
            ...deepCopy(_filterTypeItem),
            id: newId,
            columns: columns,
            isNew: true,
            label: value
          }
        ]);
        break;
      case transportationKeys.FILL_FORWARD:
        setFilterTypes([
          {
            ...deepCopy(_filterTypeItem),
            id: newId,
            columns: columns,
            isNew: true,
            label: value,
            operator: FILL_TYPES.UP
          }
        ]);
        break;

      case transportationKeys.SUBSTITUTE:
        setFilterTypes([
          {
            ..._filterType,
            id: newId,
            label: value,
            isNew: true,
            isParent: true,
            then: {
              input: '',
              column: '',
              null: null
            },
            elseValue: {
              input: '',
              column: '',
              null: null
            },
            elseInputType: 'input',
            thenInputType: 'input',
            replaceColumn: '',
            updateType: 'input',
            columnsCount: 1,
            conditions: []
          }
        ]);
        break;
    }
  };

  const getAllConditionsWithActiveFilterType = filterTypeId => {
    let _filterTypes = [...filterTypes];

    const _selectedItems = [...selectedItems];

    const activeFilterType = _filterTypes.find(type => type.id === filterTypeId);

    const allFlats = findAllObjects(activeFilterType);

    return { _filterTypes, allFlats, _selectedItems, activeFilterType };
  };

  const addArithmetic = (parentId, filterTypeId, onlyOperator) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === parentId);

    if (onlyOperator)
      return foundItem.conditions?.push({
        ...deepCopy(_filterTypeItem),
        actionItem: 'operator',
        id: uuidv4()
      });

    foundItem.conditions?.push({
      ...deepCopy(_filterTypeItem),
      actionItem: 'operator',
      id: uuidv4()
    });

    foundItem.conditions?.push({
      ...deepCopy(_filterTypeItem),
      actionItem: 'condition',
      id: uuidv4()
    });

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const groupArithmetic = (parentId, filterTypeId) => {
    const { _filterTypes, allFlats, _selectedItems, activeFilterType } =
      getAllConditionsWithActiveFilterType(filterTypeId);

    const parentIdsOfSelectedItems = allFlats
      .filter(
        el =>
          el.conditions?.length > 0 &&
          selectedItems.find(itemId => el.conditions.find(condition => condition.id === itemId))
      )
      .map(el => el.id);

    const currentParentId = parentIdsOfSelectedItems.includes(parentId)
      ? parentId
      : parentIdsOfSelectedItems?.[0];

    let foundItem = allFlats.find(item => item.id === currentParentId);

    const sortSelectedItems = (_selectedItems, foundItem) => {
      return [..._selectedItems].sort((a, b) => {
        // Get the index of each UUID in foundItem.conditions
        const indexA = foundItem.conditions.findIndex(el => el.id === a);
        const indexB = foundItem.conditions.findIndex(el => el.id === b);

        // Ensure items not in conditions move to the end
        return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
      });
    };

    const groupBetweenConditions = () => {
      const newId = uuidv4();
      const insertIndex = foundItem.conditions.findIndex(el => el.id === _selectedItems[0]);

      const sortedItems = [...sortSelectedItems(_selectedItems, foundItem)];

      const groupedItems = sortedItems.reduce((acc, id, index) => {
        const item = {
          ...allFlats.find(el => el.id === id),
          id: uuidv4()
        };

        const itemIndex = foundItem.conditions.findIndex(el => el.id === id);

        if (itemIndex > 0 && index > 0) {
          const operator = {
            ...deepCopy(foundItem.conditions[itemIndex - 1])
          };

          removeObjectByIds(_filterTypes, [operator.id]);

          acc.push({
            ...deepCopy(operator),
            actionItem: 'operator',
            id: uuidv4()
          });
        }

        acc.push(item);

        return acc;
      }, []);

      foundItem.conditions.splice(insertIndex, 0, {
        ...deepCopy(_filterType),
        id: newId,
        type: 'group',
        firstParent: false,
        label: activeFilterType.label,
        conditions: [...groupedItems]
      });

      let updatedFilterTypes = removeObjectByIds(_filterTypes, _selectedItems);

      updateObjectById(updatedFilterTypes, foundItem.id, { ...foundItem });
      setFilterTypes(updatedFilterTypes);
      setSelectedItems([]);
    };

    const newGroup = () => {
      const newId = uuidv4();

      onAdding(currentParentId, filterTypeId, true);

      foundItem.conditions.push({
        ...deepCopy(_filterType),
        id: newId,
        type: 'group',
        firstParent: false,
        label: activeFilterType.label,
        conditions: [
          {
            ...deepCopy(_filterTypeItem),
            id: uuidv4()
          }
        ]
      });

      updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
      setFilterTypes(_filterTypes);
    };

    if (foundItem?.firstParent && selectedItems?.length > 0) return groupBetweenConditions();

    if (foundItem?.firstParent) return newGroup();

    onAdding(currentParentId, filterTypeId, true);

    foundItem.conditions = [
      ...foundItem.conditions,
      {
        ...deepCopy(_filterType),
        id: uuidv4(),
        type: 'group',
        label: activeFilterType.label,
        conditions: [
          {
            ...deepCopy(_filterTypeItem),
            id: uuidv4()
          }
        ]
      }
    ];

    let updatedFilterTypes = removeObjectByIds(_filterTypes, _selectedItems);

    updateObjectById(updatedFilterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(updatedFilterTypes);
    setSelectedItems([]);
  };

  const onGrouping = (parentId, filterTypeId) => {
    if (isArithmetic || isConcatenate) return groupArithmetic(parentId, filterTypeId);

    const { _filterTypes, allFlats, _selectedItems, activeFilterType } =
      getAllConditionsWithActiveFilterType(filterTypeId);

    const newConditionDefaults = COLUMN_DEFAULT_VALUE_ACTIONS.includes(activeFilterType?.label)
      ? {
          inputType: 'column',
          ...(activeFilterType?.label === transportationKeys.REPLACE
            ? { fuzzyMatching: false }
            : {})
        }
      : {};

    const parentIdsOfSelectedItems = allFlats
      .filter(
        el =>
          el.conditions?.length > 0 &&
          selectedItems.find(itemId => el.conditions.find(condition => condition.id === itemId))
      )
      .map(el => el.id);

    const currentParentId = parentIdsOfSelectedItems.includes(parentId)
      ? parentId
      : parentIdsOfSelectedItems?.[0] || parentId;

    let foundItem = allFlats.find(item => item.id === currentParentId);

    if (foundItem?.firstParent) {
      const newId = uuidv4();

      foundItem.conditions?.push({
        ..._filterType,
        id: newId,
        type: 'group',
        label: activeFilterType.label || filterTypes?.[0]?.label,
        conditionLabel: conditionKeys.AND,
        firstParent: true,
        conditions:
          _selectedItems?.length > 0
            ? [
                ..._selectedItems.map(id => ({
                  ...allFlats.find(el => el.id === id),
                  id: uuidv4(),
                  type: 'item'
                }))
              ]
            : [
                {
                  ...deepCopy(_filterTypeItem),
                  id: uuidv4(),
                  ...newConditionDefaults
                }
              ]
      });
    } else if (foundItem) {
      foundItem.conditions = [
        ...foundItem.conditions,
        {
          ..._filterType,
          id: uuidv4(),
          type: 'group',
          label: activeFilterType.label,
          conditionLabel: conditionKeys.AND,
          firstParent: true,
          conditions: [
            {
              ...deepCopy(_filterTypeItem),
              id: uuidv4(),
              ...newConditionDefaults
            }
          ]
        }
      ];
    }

    let updatedFilterTypes = removeObjectByIds(_filterTypes, _selectedItems);

    updateObjectById(updatedFilterTypes, foundItem?.id, { ...foundItem });
    setFilterTypes(updatedFilterTypes);
    setSelectedItems([]);
  };

  const onAdding = (parentId, filterTypeId, onlyOperator = false) => {
    if (isArithmetic || isConcatenate) return addArithmetic(parentId, filterTypeId, onlyOperator);

    const conditionId = uuidv4();

    const { _filterTypes, allFlats, activeFilterType } =
      getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item?.id === parentId);

    const label = foundItem?.label || activeFilterType?.label;
    const newConditionDefaults = COLUMN_DEFAULT_VALUE_ACTIONS.includes(label)
      ? {
          inputType: 'column',
          ...(label === transportationKeys.REPLACE ? { fuzzyMatching: false } : {})
        }
      : {};

    foundItem?.conditions?.push({
      ...deepCopy(_filterTypeItem),
      id: conditionId,
      ...newConditionDefaults
    });

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeColumn = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.column = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeFuzzyMatching = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.fuzzyMatching = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeCurrentFormats = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.currentFormats = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeOutputFormat = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.outputFormat = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeKeep = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.keep = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  //  split_columns_values transformation only
  const changeColumnsCount = (filterTypeId, itemId, count) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);
    if (!foundItem) return;

    let existingColumns = foundItem.columns || [];
    const isDatetime = foundItem.elseInputType === 'datetime';

    if (existingColumns.length < count) {
      // Extend the array without removing existing values
      foundItem.columns = [...existingColumns, ...Array(count - existingColumns.length).fill('')];
    } else if (existingColumns.length > count) {
      // Trim the array but keep existing values
      foundItem.columns = existingColumns.slice(0, count);
    }

    if (isDatetime)
      foundItem.columns = foundItem.columns.map((_, idx) => getMonthValue(periodName, idx, year));

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  //  split_columns_values transformation only
  const onChangeSingleColumn = (filterTypeId, itemId, index, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.columns[index] = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeOperator = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.filter(item => item).find(item => item?.id === itemId);

    if (foundItem) {
      foundItem.operator = val;
      updateObjectById(_filterTypes, foundItem?.id, { ...foundItem });
      setFilterTypes(_filterTypes);
    }
  };

  const onChangeFunction = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.filter(item => item).find(item => item?.id === itemId);

    if (foundItem) {
      foundItem.function = val;
      updateObjectById(_filterTypes, foundItem?.id, { ...foundItem });
      setFilterTypes(_filterTypes);
    }
  };

  const onChangeSelectedColumns = (filterTypeId, itemId, val, text) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.then.ai_input.selected_columns = [
      ...new Set([...foundItem.then.ai_input.selected_columns, val])
    ].filter(el => el && text.includes(val));

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeInput = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    if (foundItem.inputType === 'ai_input') {
      foundItem.value.ai_input.input = val;
      foundItem.value.ai_input.selected_columns = foundItem.value.ai_input.selected_columns?.filter(
        el => el && val.includes(el)
      );
    } else foundItem.value[foundItem.inputType] = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onAddListItem = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.value.list = [...(foundItem?.value?.list || []), val];

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onRemoveListItem = (filterTypeId, itemId, idx) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.value.list = foundItem.value.list.filter((_, _idx) => _idx !== idx);

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeUpdateType = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.updateType = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeColumnsCount = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.columnsCount = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeThen = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    if (foundItem.thenInputType === 'ai_input') {
      foundItem.then.ai_input.input = val;
      foundItem.then.ai_input.selected_columns = foundItem.then.ai_input.selected_columns?.filter(
        el => el && val.includes(el)
      );
    } else foundItem.then[foundItem.thenInputType] = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeThenInputType = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.thenInputType = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeBrowserUse = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    if (foundItem.thenInputType === 'ai_input') {
      foundItem.then.ai_input.browser_use = val;
    }

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const addCondition = (id, type, filterTypeId) => {
    if (type === 'group') onGrouping(id, filterTypeId);
    else onAdding(id, filterTypeId);
  };

  const onSelectCondition = (filterTypeId, value, itemId) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);
    foundItem.conditionLabel = value;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onSelectItem = itemId => {
    if (selectedItems.includes(itemId)) setSelectedItems(prev => prev.filter(el => el !== itemId));
    else setSelectedItems(prev => [...prev, itemId]);
  };

  const deleteArithmeticItems = filterTypeId => {
    const { _filterTypes, _selectedItems, allFlats } =
      getAllConditionsWithActiveFilterType(filterTypeId);
    const _updatedRemovalbleItemsId: string[] = [];

    _selectedItems.forEach(id => {
      _updatedRemovalbleItemsId.push(id);

      const index = allFlats.findIndex(item => item.id === id);
      if (index > 0) _updatedRemovalbleItemsId.push(allFlats[index - 1].id);
    });

    let updatedFilterTypes = _updatedRemovalbleItemsId
      ? removeObjectByIds(_filterTypes, _updatedRemovalbleItemsId)
      : removeObjectByIds(_filterTypes, _selectedItems);

    setFilterTypes(updatedFilterTypes);
    setSelectedItems([]);
  };

  const onDeleteSelectedItems = (filterTypeId, removabledItemsId) => {
    if (isArithmetic || isConcatenate) return deleteArithmeticItems(filterTypeId);

    const { _filterTypes, _selectedItems } = getAllConditionsWithActiveFilterType(filterTypeId);

    let updatedFilterTypes = removabledItemsId
      ? removeObjectByIds(_filterTypes, removabledItemsId)
      : removeObjectByIds(_filterTypes, _selectedItems);

    setFilterTypes(updatedFilterTypes);
    setSelectedItems([]);
  };

  const onAddFilterType = isChild => {
    const isChangeDateTypeAction = filterTypes?.[0]?.label === transportationKeys.CHANGE_DATA_TYPES;
    const isReplaceAction = filterTypes?.[0]?.label === transportationKeys.REPLACE;

    if (isChild)
      setFilterTypes(prev => [
        ...prev,
        {
          ...deepCopy(_filterTypeItem),
          id: uuidv4(),
          inputType: isChangeDateTypeAction ? 'column' : 'input',
          value: { input: '', column: '', null: null, list: [] }
        }
      ]);
    else {
      setFilterTypes(prev => [
        ...prev,
        {
          id: uuidv4(),
          conditionLabel: conditionKeys.AND,
          type: 'group',
          inputType: 'input',
          then: {
            input: '',
            column: '',
            null: null
          },
          elseValue: {
            input: '',
            column: '',
            null: null
          },
          thenInputType: isReplaceAction ? 'column' : 'input',
          elseInputType: isReplaceAction ? 'column' : 'input',
          replaceColumn: '',
          isParent: true,
          isNew: true,
          firstParent: true,
          label: prev?.[0]?.label,
          conditions: [
            {
              id: uuidv4(),
              function: null,
              column: null,
              operator: null,
              inputType: isReplaceAction ? 'column' : 'input', // column|input
              value: {
                input: '',
                column: '',
                null: null
              }
            }
          ]
        }
      ]);
    }
  };

  const onChangeInputType = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.inputType = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeElseValue = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.elseValue[foundItem.elseInputType] = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeReplaceColumn = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.replaceColumn = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeElseInputType = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.elseInputType = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  // type = add | remove
  const onToggleColumnItem = (filterTypeId, itemId, val, type = '') => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    switch (type) {
      case 'add':
        foundItem.columns = [...new Set([...foundItem.columns, val])];
        break;
      case 'remove':
        foundItem.columns = foundItem.columns.filter(el => el !== val);
        break;
      default:
        foundItem.columns = foundItem.columns?.includes(val)
          ? [...(foundItem.columns.filter(el => el !== val) || [])]
          : [...foundItem.columns, val];
        break;
    }

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  // type = add | remove
  const onToggleColumnItem2 = (filterTypeId, itemId, val, type) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);
    switch (type) {
      case 'add':
        foundItem.columns2 = [...new Set([...foundItem.columns2, val])];
        break;
      case 'remove':
        foundItem.columns2 = foundItem.columns2.filter(el => el !== val);
        break;
      default:
        foundItem.columns2 = foundItem.columns2?.includes(val)
          ? foundItem.columns2.filter(el => el !== val)
          : [...foundItem.columns2, val];
        break;
    }
    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onClearColumns = (filterTypeId, itemId, exceptions = []) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.columns = foundItem.columns.filter(el => exceptions.includes(el));

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onClearColumns2 = (filterTypeId, itemId, exceptions = []) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);
    foundItem.columns2 = foundItem.columns2.filter(el => exceptions.includes(el));

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeNumberOfSplits = (filterTypeId, itemId, _val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    const val = Number(_val);

    foundItem.numberOfSplits = String(val);
    foundItem.columns = [
      ...foundItem.columns,
      ...Array.from({ length: val + 1 }, (_, i) => null)
    ].slice(0, val + 1);

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeDirection = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.direction = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeExpandedNewColumns = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.expandedNewColumns = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };
  const disableSaveBtn = useMemo(() => {
    if (filterTypes[0]?.label !== transportationKeys.JOIN) {
      setDisableMessage();
      return false;
    }

    const conditionsCount = filterTypes[0]?.conditions?.length;
    const firstColumnsCount = filterTypes[0]?.columns?.length;
    const secondColumnsCount = filterTypes[0]?.columns2?.length;

    if (firstColumnsCount > 0 && secondColumnsCount > 0) {
      return false;
    }

    setDisableMessage('Select result columns (minimum: as much as conditions count)');

    return true;
  }, [filterTypes]);

  const checkForRequiredFields = () => {
    const isReplace = filterTypes[0].label === transportationKeys.REPLACE;
    const isFilter = filterTypes[0].label === transportationKeys.FILTER;
    const isFillForward = filterTypes[0].label === transportationKeys.FILL_FORWARD;
    const isAbsolute = filterTypes[0].label === transportationKeys.ABSOLUTE;
    const isCreateColumn = filterTypes[0].label === transportationKeys.ADD_COLUMN;
    const isRemoveDuplicates = filterTypes[0].label === transportationKeys.REMOVE_DUPLICATES;
    const isJoin = filterTypes[0].label === transportationKeys.JOIN;
    const isArithmetic = filterTypes[0].label === transportationKeys.ARITHMETIC;
    const isDateShifting = filterTypes[0].label === transportationKeys.DATE_SHIFTING;
    const isSplitByValue = filterTypes[0].label === transportationKeys.SPLIT_BY_VALUE;
    const isReplaceText = filterTypes[0].label === transportationKeys.SUBSTITUTE;
    const isDuplicateRows = filterTypes[0].label === transportationKeys.DUPLICATE_ROWS;
    const isNegative = filterTypes[0].label === transportationKeys.NEGATIVE;
    const isSumIf = filterTypes[0].label === transportationKeys.SUM_IF;
    const isRenameColumn = filterTypes[0].label === transportationKeys.RENAME_COLUMNS;
    const isSplit = filterTypes[0].label === transportationKeys.SPLIT;
    const isDataTypeChange = filterTypes[0].label === transportationKeys.CHANGE_DATA_TYPES;

    let hasEmptyField = false;

    filterTypes.forEach((filterType, fIdx) => {
      const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterType.id);

      const itemsGroup = allFlats.filter(el => el.type !== 'group');

      if (isReplace && fIdx === 0) {
        if (!filterType.replaceColumn) filterType.errors = ['replace_column'];
        else filterType.errors = [];

        if (filterType.errors?.length > 0) hasEmptyField = true;

        updateObjectById(_filterTypes, filterType.id, { ...filterType });
      }

      if (isSplitByValue) {
        if (!filterType.replaceColumn) filterType.errors.push('replace_column');
        else filterType.errors = filterType.errors?.filter(el => el !== 'replace_column');

        if (!filterType.then.input) filterType.errors.push('then');
        else filterType.errors = filterType.errors?.filter(el => el !== 'then');

        if (!(filterType.numberOfSplits > 0)) filterType.errors?.push('numberOfSplits');
        else filterType.errors = filterType.errors?.filter(el => el !== 'numberOfSplits');

        if (filterType.columns.filter(el => !el).length > 0 && filterType?.expandedNewColumns) {
          filterType.errors = filterType?.errors?.filter(el => !el.includes('columns'));
          filterType?.columns.map((col, idx) => {
            if (!col) {
              filterType.errors.push(`columns[${idx}]`);
            }

            return col;
          });
        } else {
          filterType.errors = filterType.errors?.filter(err => !err.includes('columns'));
        }

        if (filterType.errors?.length > 0) hasEmptyField = true;

        updateObjectById(_filterTypes, filterType.id, { ...filterType });
      } else if (isJoin) {
        allFlats.forEach(item => {
          const errors = [];

          if (item.type === 'group' && (item.inputType === 'input' || item.inputType === 'column'))
            errors.push('inputType');
          if (item.type !== 'group' && !item.column && item.type !== 'group') errors.push('column');
          if (item.type !== 'group' && !item.operator && item.type !== 'group')
            errors.push('operator');
          if (errors.length > 0) hasEmptyField = true;

          item.errors = errors;

          updateObjectById(_filterTypes, item.id, { ...item });
        });
      } else if (isArithmetic || isConcatenate) {
        allFlats.forEach(item => {
          const errors = [];

          if (!item.value?.column && !item.value?.input && item.actionItem === 'condition') {
            errors.push('condition');
          }

          if (!item.then?.column && !item.then?.input && item.type === 'group') {
            errors.push('then');
          }

          if (
            item.type === 'item' &&
            !item.value.column &&
            !item.value.input &&
            item.actionItem === 'operator'
          ) {
            errors.push('operator');
          }

          if (errors.length > 0) hasEmptyField = true;

          item.errors = errors;

          updateObjectById(_filterTypes, item.id, { ...item });
        });
      } else if (isDateShifting) {
        allFlats.forEach(item => {
          const errors = [];

          if (!item.columnName && item.type === 'group') errors.push('columnName');
          if (!item.shiftingMethod && item.type === 'group') errors.push('shiftingMethod');
          if (!item.timePeriod && item.type === 'group') errors.push('timePeriod');
          if (!item.periodAmount && item.type === 'group') errors.push('periodAmount');
          if (!item.operator && item.type === 'item') errors.push('operator');
          if (!item.column && item.type === 'item') errors.push('column');
          if (
            item.type === 'item' &&
            item.operator !== operatorKeys.EQUAL_TO &&
            item.operator !== operatorKeys.NOT_EQUAL_TO &&
            !item.value
          )
            errors.push('value');

          if (errors.length > 0) hasEmptyField = true;

          item.errors = errors;

          updateObjectById(_filterTypes, item.id, { ...item });
        });
      } else if (isReplaceText) {
        allFlats.forEach(item => {
          const errors = [];

          if (!item.replaceColumn) errors.push('replaceColumn');
          if (!item.elseValue.input) errors.push('elseValue');
          // if (!item.then.input) errors.push('then');

          if (errors.length > 0) hasEmptyField = true;

          item.errors = errors;

          updateObjectById(_filterTypes, item.id, { ...item });
        });
      } else if (isRenameColumn) {
        allFlats.forEach(item => {
          const errors = [];

          if (!item.column && item.type === 'item') errors.push('column');
          if (item.type === 'item' && !item.value.column && !item.value.input) errors.push('value');

          if (errors.length > 0) hasEmptyField = true;

          item.errors = errors;

          updateObjectById(_filterTypes, item.id, { ...item });
        });
      } else if (isSplit) {
        allFlats.forEach(item => {
          const errors = [];

          if (item.type === 'group' && !item.replaceColumn) errors.push('replaceColumn');
          if (!item.operator && item.type === 'item') errors.push('operator');
          if (!item.column && item.type === 'item') errors.push('column');
          if (
            item.operator !== operatorKeys.EQUAL_TO &&
            item.operator !== operatorKeys.NOT_EQUAL_TO &&
            !item.value &&
            item.type === 'item'
          )
            errors.push('value');

          if (errors.length > 0) hasEmptyField = true;

          item.errors = errors;

          updateObjectById(_filterTypes, item.id, { ...item });
        });
      } else
        itemsGroup.forEach(item => {
          const errors = [];
          if (isReplace || isFilter || isDuplicateRows || isSumIf) {
            if (!item.operator) errors.push('operator');
            if (!item.column) errors.push('column');
            if (
              item.operator !== operatorKeys.EQUAL_TO &&
              item.operator !== operatorKeys.NOT_EQUAL_TO &&
              !item.value
            )
              errors.push('value');

            if (errors.length > 0) hasEmptyField = true;
          } else if (isFillForward || isNegative) {
            if (!item.column) errors.push('column');

            if (errors.length > 0) hasEmptyField = true;
          } else if (isAbsolute) {
            if (!item.column) errors.push('column');

            if (errors.length > 0) hasEmptyField = true;
          } else if (isCreateColumn) {
            if (!item.column) errors.push('column');

            if (errors.length > 0) hasEmptyField = true;
          } else if (isRemoveDuplicates) {
            if (item.columns.length === 0) errors.push('columns');
            if (!item.keep) errors.push('keep');

            if (errors.length > 0) hasEmptyField = true;
          } else if (isJoin) {
            // if (!item.then?.column && !item.then?.input) errors.push("then");

            if (errors.length > 0) hasEmptyField = true;
          } else if (isArithmetic) {
            //   if (!item.then?.column && item.actionItem !== 'operator')
            //     errors.push('then');
            //   console.log(item, errors);
            //   if (errors.length > 0) hasEmptyField = true;
          } else if (isDateShifting) {
            // if (!item.columnName) errors.push('columnName');
            // if (!item.shiftingMethod) errors.push('shiftingMethod');
            // if (!item.timePeriod) errors.push('timePeriod');
            // if (!item.periodAmount) errors.push('periodAmount');
            // if (errors.length > 0) hasEmptyField = true;
          } else if (isDataTypeChange) {
            if (!item.column) errors.push('column');
            if (!item.value.column) errors.push('value');

            if (errors.length > 0) hasEmptyField = true;
          }

          item.errors = errors;

          updateObjectById(_filterTypes, item.id, { ...item });
        });

      setFilterTypes(_filterTypes);
    });

    return hasEmptyField;
  };

  const clear = () => {
    setFilterTypes([]);
    setSelectedItems([]);
    reset();
  };

  const onChangeColumnName = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item?.id === itemId);

    foundItem.columnName = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeShiftingMethod = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.shiftingMethod = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeTimePeriod = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.timePeriod = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangePeriodAmount = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.periodAmount = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const handleDragEnd = (result, parentFilterTypeId) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    const { allFlats, _filterTypes } = getAllConditionsWithActiveFilterType(
      parentFilterTypeId || filterTypes[0].id
    );

    const _els = [..._filterTypes];

    if (result.source.droppableId === result.destination.droppableId) {
      // Reordering within the same list
      const listId = result.source.droppableId;

      let foundItem = allFlats.find(item => item.id === listId);

      const updatedItems = reorderList(foundItem.conditions, sourceIndex, destinationIndex);

      foundItem.conditions = updatedItems;

      updateObjectById(_els, listId, {
        ...foundItem
      });

      setFilterTypes(_els);
    }
  };

  return {
    filterTypes,
    selectedItems,
    disableMessage,
    disableSaveBtn,
    clear,
    onChangeThen,
    addCondition,
    onSelectItem,
    onChangeKeep,
    onChangeInput,
    handleDragEnd,
    onChangeColumn,
    onAddListItem,
    onClearColumns,
    onClearColumns2,
    setFilterTypes,
    onAddFilterType,
    onChangeOperator,
    onRemoveListItem,
    onChangeFunction,
    onChangeInputType,
    onSelectCondition,
    onChangeElseValue,
    onToggleColumnItem,
    onToggleColumnItem2,
    onSelectFilterType,
    changeColumnsCount,
    onChangeUpdateType,
    onChangeColumnsCount,
    onChangeSingleColumn,
    onChangeReplaceColumn,
    onChangeElseInputType,
    onDeleteSelectedItems,
    onChangeThenInputType,
    onChangeBrowserUse,
    checkForRequiredFields,
    onChangeShiftingMethod,
    onChangePeriodAmount,
    onChangeTimePeriod,
    onChangeColumnName,
    onChangeSelectedColumns,
    onChangeCurrentFormats,
    onChangeOutputFormat,
    onChangeFuzzyMatching,
    onChangeNumberOfSplits,
    onChangeDirection,
    onChangeExpandedNewColumns
  };
}
